import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { config } from '../config';

const API_BASE_URL = config.API_BASE_URL;

export const isApiDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const enabled = localStorage.getItem('debugApi') === 'true';
    if (enabled) return true;
    // Permite activar con ?debugApi=true en la URL (útil en QR)
    const params = new URLSearchParams(window.location.search);
    return params.get('debugApi') === 'true';
  } catch {
    return false;
  }
};

const maskHeaders = (headers: Record<string, unknown> | undefined) => {
  if (!headers) return undefined;
  const clone: Record<string, unknown> = { ...headers };
  if (clone.Authorization) clone.Authorization = '***';
  if ((clone as any)['authorization']) (clone as any)['authorization'] = '***';
  return clone;
};

const getDebugContext = () => {
  try {
    const tenantId = useTenantStore.getState().tenantId;
    const user = useAuthStore.getState().user;
    const hasToken = !!useAuthStore.getState().token;
    return { tenantId, userId: user?.id, userRole: user?.rol, hasToken };
  } catch {
    return {};
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  // Sin headers por defecto - el navegador los pondrá automáticamente
});

// Interceptor para agregar el token de autenticación
api.interceptors.request.use(
  (config) => {
    (config as any)._meta = { start: Date.now() };
    const token = useAuthStore.getState().token;
    const tenantId = useTenantStore.getState().tenantId;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId && config.headers) {
      (config.headers as any)['X-Tenant-Id'] = String(tenantId);
    }

    // Si se está enviando FormData, NO fijar Content-Type
    if (config.data instanceof FormData && config.headers) {
      delete (config.headers as any)['Content-Type'];
    }

    if (isApiDebugEnabled()) {
      console.debug('[API][REQ]', config.method?.toUpperCase(), config.url, {
        params: config.params,
        data: config.data,
        headers: maskHeaders(config.headers as any),
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => {
    if (isApiDebugEnabled()) {
      const start = (response.config as any)._meta?.start ?? Date.now();
      const duration = Date.now() - start;
      console.debug('[API][RES]', response.status, response.config.method?.toUpperCase(), response.config.url, `${duration}ms`, {
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 403 || error?.response?.status === 423) {
      console.warn('[API][AUTH]', `HTTP ${error.response.status} en ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        context: getDebugContext(),
        body: error.response?.data,
      });
    }

    if (isApiDebugEnabled() && error?.config) {
      const start = (error.config as any)._meta?.start ?? Date.now();
      const duration = Date.now() - start;
      console.debug('[API][ERR]', error?.response?.status, error.config.method?.toUpperCase(), error.config.url, `${duration}ms`, {
        data: error?.response?.data,
      });
    }

    if (error.response?.status === 401) {
      // Cerrar sesión y limpiar tenant al expirar token
      try { useAuthStore.getState().logout(); } catch {}
      try { useTenantStore.getState().clearTenant(); } catch {}
      // Redirigir a login para evitar estados intermedios en rutas protegidas.
      // En /qr no: el personal de aseo vuelve a ingresar su RUT ahí mismo, y /login
      // (email y contraseña) lo dejaría sin salida y perdería el área del QR.
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (pathname && pathname !== '/login' && pathname !== '/qr') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

export default api; 
